import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import {
  AppSessionResponse,
  LinkedAccountProfile,
  LinkedAccountsResponse,
} from "types/auth";
import { LocalStorage } from "utils/LocalStorage";
import { amplitude } from "utils/amplitudeAnalytics";
import { decodeJwtPayload } from "utils/appSessionClaims";

const SESSION_TRACKED_KEY = "vetnebulaSessionTracked";
const PREFERRED_ACCOUNT_KEY = "vetnebula.preferredLinkedAccount";
const RETURN_PATH_KEY = "vetnebula.returnPath";

const FEEDBACK_DETAIL_PATH =
  /^\/(?:feedback-portal|feedback)\/[0-9a-f-]{36}(?:\?.*)?$/i;
const ORGANIZATION_FEEDBACK_DETAIL_PATH =
  /^\/organization\/[0-9a-f-]{36}\/feedback\/[0-9a-f-]{36}(?:\?.*)?$/i;

export function rememberFeedbackReturnPath(
  path = `${window.location.pathname}${window.location.search}`,
): void {
  if (
    !FEEDBACK_DETAIL_PATH.test(path) &&
    !ORGANIZATION_FEEDBACK_DETAIL_PATH.test(path)
  ) {
    return;
  }
  sessionStorage.setItem(RETURN_PATH_KEY, path);
}

export function consumeFeedbackReturnPath(): string | null {
  const path = sessionStorage.getItem(RETURN_PATH_KEY);
  if (!path) return null;
  sessionStorage.removeItem(RETURN_PATH_KEY);
  return path;
}

export function clearFeedbackReturnPath(): void {
  sessionStorage.removeItem(RETURN_PATH_KEY);
}

export function isCurrentBrowserSessionTracked(): boolean {
  return sessionStorage.getItem(SESSION_TRACKED_KEY) === "true";
}

export function clearCurrentBrowserSessionTracking(): void {
  sessionStorage.removeItem(SESSION_TRACKED_KEY);
}

function decodeExpiration(token: string): string {
  try {
    const payload = JSON.parse(
      decodeURIComponent(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map(
            (character) =>
              `%${("00" + character.charCodeAt(0).toString(16)).slice(-2)}`,
          )
          .join(""),
      ),
    ) as { exp?: number };
    return payload.exp?.toString() || "0";
  } catch {
    return "0";
  }
}

function expirationToSeconds(
  expiresAt: AppSessionResponse["expires_at"],
  token: string,
): string {
  if (expiresAt === undefined || expiresAt === null) {
    return decodeExpiration(token);
  }
  if (typeof expiresAt === "number") {
    return String(expiresAt > 10_000_000_000 ? expiresAt / 1000 : expiresAt);
  }
  const numeric = Number(expiresAt);
  if (Number.isFinite(numeric)) {
    return String(numeric > 10_000_000_000 ? numeric / 1000 : numeric);
  }
  const date = Date.parse(expiresAt);
  return Number.isNaN(date) ? decodeExpiration(token) : String(date / 1000);
}

type PreferredAccounts = Record<string, string>;

function readPreferredAccounts(): PreferredAccounts {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(PREFERRED_ACCOUNT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PreferredAccounts;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function sessionIdentityFromToken(token: string): {
  sessionUserId: string;
  activeUserId: string;
} | null {
  const payload = decodeJwtPayload(token);
  const sessionUserId = String(payload?.session_user_id || "").trim();
  const activeUserId = String(payload?.active_user_id || "").trim();
  if (!sessionUserId || !activeUserId) return null;
  return { sessionUserId, activeUserId };
}

export function rememberPreferredLinkedAccount(token: string): void {
  const identity = sessionIdentityFromToken(token);
  if (!identity) return;
  const preferred = readPreferredAccounts();
  preferred[identity.sessionUserId] = identity.activeUserId;
  window.localStorage.setItem(PREFERRED_ACCOUNT_KEY, JSON.stringify(preferred));
}

export function preferredLinkedAccountId(sessionUserId: string): string {
  return readPreferredAccounts()[sessionUserId] || "";
}

async function restorePreferredLinkedAccount(
  session: AppSessionResponse,
): Promise<AppSessionResponse> {
  const identity = sessionIdentityFromToken(session.token);
  if (!identity) return session;
  const preferredUserId = preferredLinkedAccountId(identity.sessionUserId);
  if (!preferredUserId || preferredUserId === identity.activeUserId) {
    return session;
  }
  try {
    return await switchLinkedAccount(preferredUserId);
  } catch {
    return session;
  }
}

export function storeAppSession(session: AppSessionResponse): void {
  if (!session.token) {
    throw new Error("The server did not return an application token.");
  }
  LocalStorage.setToken(session.token);
  LocalStorage.setTokenExpirationTime(
    expirationToSeconds(session.expires_at, session.token),
  );
}

function trackActiveSession(session: AppSessionResponse): void {
  const profile = session.active_user || session.user;
  if (profile?.user_id) {
    amplitude.setUserId(profile.user_id);
  }
  amplitude.track("Active Session", {
    user_id: profile?.user_id,
    role: profile?.role,
  });
}

export async function exchangeCognitoToken(
  cognitoIdToken: string,
): Promise<AppSessionResponse> {
  const { data } = await API.post<AppSessionResponse>(
    API_ROUTES.AUTH.SESSION(),
    undefined,
    {
      headers: { Authorization: `Bearer ${cognitoIdToken}` },
      preserveSessionOnUnauthorized: true,
    },
  );
  storeAppSession(data);
  const restored = await restorePreferredLinkedAccount(data);
  if (restored.token !== data.token) {
    storeAppSession(restored);
  }
  sessionStorage.setItem(SESSION_TRACKED_KEY, "true");
  trackActiveSession(restored);
  return restored;
}

/**
 * Records Terms of Use acceptance server-side and stores the re-issued token.
 * The new token carries the profile's real Hasura role; until this succeeds the
 * session only holds the restricted `pending_terms` role.
 */
export async function acceptTermsOfUse(): Promise<AppSessionResponse> {
  const { data } = await API.post<AppSessionResponse>(
    API_ROUTES.AUTH.ACCEPT_TERMS(),
    undefined,
    { preserveSessionOnUnauthorized: true },
  );
  storeAppSession(data);
  return data;
}

export async function getLinkedAccounts(): Promise<LinkedAccountsResponse> {
  const { data } = await API.get<
    LinkedAccountsResponse | LinkedAccountProfile[]
  >(API_ROUTES.ME.ACCOUNTS(), {
    // The app JWT has no email, so session restore always calls this. A 401
    // here must not sign the user out — that bounce lands them on Home.
    preserveSessionOnUnauthorized: true,
  });
  if (Array.isArray(data)) {
    return { accounts: data };
  }
  return {
    ...data,
    accounts:
      data.accounts ||
      ((data as LinkedAccountsResponse & {
        profiles?: LinkedAccountProfile[];
      }).profiles ??
        []),
  };
}

export async function switchLinkedAccount(
  userId: string,
): Promise<AppSessionResponse> {
  const { data } = await API.post<AppSessionResponse>(
    API_ROUTES.AUTH.SWITCH(),
    { user_id: userId },
    { preserveSessionOnUnauthorized: true },
  );
  rememberPreferredLinkedAccount(data.token);
  trackActiveSession(data);
  return data;
}
